import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShareNetwork, Copy, Trash, ArrowSquareOut } from "@phosphor-icons/react";

export function InvestorLinkDialog({ pid }) {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const url = link ? `${window.location.origin}${link.path}` : "";

  const load = async () => {
    try {
      const r = await api.get(`/projects/${pid}/investor-link`);
      setLink(r.data.active ? { token: r.data.token, path: r.data.path, views: r.data.views, last_viewed: r.data.last_viewed } : null);
    } catch (e) { /* silent */ }
  };
  const create = async () => {
    setBusy(true);
    try { const r = await api.post(`/projects/${pid}/investor-link`); setLink({ token: r.data.token, path: r.data.path, views: 0, last_viewed: null }); toast.success("Summary link created"); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    setBusy(true);
    try { await api.delete(`/projects/${pid}/investor-link`); setLink(null); toast.success("Summary link revoked"); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); }
    catch (e) { toast.error("Copy failed - select and copy manually"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-8" data-testid="command-investor-share">
          <ShareNetwork size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">Technical Summary Link</span>
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="investor-link-dialog">
        <DialogHeader><DialogTitle>Resource Technical Summary Link</DialogTitle></DialogHeader>
        <DialogDescription className="text-sm text-muted-foreground">
          A public, read-only technical summary of this project - headline KPIs, the intelligence score, the licence map and ranked targets. No login required; anyone with the link can view it.
        </DialogDescription>
        {link ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input readOnly value={url} data-testid="investor-link-url"
                className="flex-1 h-9 rounded-md border border-border bg-background px-2 text-xs font-mono truncate text-foreground" />
              <Button size="icon" variant="outline" className="h-9 w-9 shrink-0" onClick={copy} data-testid="investor-link-copy"><Copy size={15} /></Button>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => window.open(url, "_blank")} data-testid="investor-link-open"><ArrowSquareOut size={14} className="mr-1.5" /> Preview</Button>
              <Button size="sm" variant="ghost" className="text-[#F43F5E] hover:text-[#F43F5E]" onClick={revoke} disabled={busy} data-testid="investor-link-revoke"><Trash size={14} className="mr-1.5" /> Revoke</Button>
            </div>
            <p className="text-[11px] text-muted-foreground" data-testid="investor-link-views">
              Opened <span className="font-mono text-foreground">{link.views ?? 0}</span> time{(link.views ?? 0) === 1 ? "" : "s"}{link.last_viewed ? ` · last ${new Date(link.last_viewed).toLocaleString()}` : ""}
            </p>
          </div>
        ) : (
          <Button onClick={create} disabled={busy} data-testid="investor-link-create">{busy ? "Creating…" : "Create public summary link"}</Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
